import { useState } from "react";
import {
  Box,
  Popover,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { colors } from "../ui/colors";

export const DateSelector = ({ value = dayjs(), onChange, sx }) => {
  const parsedValue = dayjs(value);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const isToday = parsedValue.isSame(dayjs(), "day");

  const handlePrevDay = () => {
    onChange?.(parsedValue.subtract(1, "day"));
  };

  const handleNextDay = () => {
    onChange?.(parsedValue.add(1, "day"));
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleCalendarChange = (newDate) => {
    onChange?.(newDate);
    handleClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <Box
        display="flex"
        alignItems="center"
        sx={{
          gap: 1,
          backgroundColor: colors.warmWhite.base,
          padding: "6px 10px",
          borderRadius: "10px",
          border: `1px solid ${colors.gray[200]}`,
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: colors.gray[300],
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          },
          ...sx,
        }}
      >
        {/* Día anterior */}
        <Tooltip title="Día anterior">
          <IconButton
            onClick={handlePrevDay}
            size="small"
            disableRipple
            sx={{
              backgroundColor: colors.gray[50],
              borderRadius: "999px",
              border: `1px solid ${colors.gray[200]}`,
              color: colors.gray[500],
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: colors.gray[100],
                color: colors.gray[700],
                borderColor: colors.gray[300],
              },
              "&.Mui-disabled": {
                backgroundColor: colors.gray[50],
                borderColor: colors.gray[200],
                color: colors.gray[300],
              },
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Fecha actual */}
        <Tooltip title={isToday ? "Hoy" : "Seleccionar fecha"}>
          <Chip
            label={parsedValue.format("DD MMM YYYY")}
            onClick={handleOpen}
            clickable
            size="small"
            sx={{
              borderRadius: "6px",
              px: 1.2,
              height: "30px",
              backgroundColor: isToday ? colors.gray[50] : colors.gray[50],
              border: `1px solid ${
                isToday ? colors.orange[400] : colors.gray[200]
              }`,
              cursor: "pointer",
              userSelect: "none",
              "& .MuiChip-label": {
                fontSize: "13px",
                fontWeight: isToday ? 600 : 500,
                color: colors.gray[700],
              },
              boxShadow: isToday
                ? `0 0 0 1px ${colors.orange[100]}`
                : "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: colors.gray[100],
                borderColor: isToday ? colors.orange[500] : colors.gray[300],
                boxShadow: isToday
                  ? `0 0 0 1px ${colors.orange[200]}`
                  : "0 3px 8px rgba(0,0,0,0.06)",
              },
            }}
          />
        </Tooltip>

        {/* Día siguiente */}
        <Tooltip title="Día siguiente">
          <IconButton
            onClick={handleNextDay}
            size="small"
            disableRipple
            sx={{
              backgroundColor: colors.gray[50],
              borderRadius: "999px",
              border: `1px solid ${colors.gray[200]}`,
              color: colors.gray[500],
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: colors.gray[100],
                color: colors.gray[700],
                borderColor: colors.gray[300],
              },
              "&.Mui-disabled": {
                backgroundColor: colors.gray[50],
                borderColor: colors.gray[200],
                color: colors.gray[300],
              },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            zIndex: 2147483647,
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                border: `1px solid ${colors.gray[200]}`,
                mt: 1,
                zIndex: 2147483647,
                backgroundColor: colors.warmWhite.base,
              },
            },
          }}
        >
          <DateCalendar
            value={parsedValue}
            onChange={handleCalendarChange}
            sx={{
              "& .MuiPickersCalendarHeader-root": {
                color: colors.gray[700],
              },
              "& .MuiPickersCalendarHeader-label": {
                fontWeight: 500,
                fontSize: "14px",
              },
              "& .MuiPickersArrowSwitcher-button": {
                color: colors.gray[500],
                "&:hover": {
                  backgroundColor: colors.gray[100],
                },
              },

              "& .MuiDayCalendar-weekDayLabel": {
                color: colors.gray[500],
                fontWeight: 500,
                fontSize: "12px",
              },

              "& .MuiPickersDay-root": {
                color: colors.gray[700],
                fontWeight: 400,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: colors.gray[100],
                },
              },

              "& .MuiPickersDay-root.Mui-selected": {
                backgroundColor: colors.orange[500] + " !important",
                color: colors.warmWhite.base + " !important",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: colors.orange[500] + " !important",
                },
              },

              "& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)": {
                border: `2px solid ${colors.orange[400]} !important`,
                backgroundColor: "transparent !important",
                boxShadow: `0 0 0 1px ${colors.orange[100]}`,
              },

              "& .MuiPickersDay-root.MuiPickersDay-today.Mui-selected": {
                border: "none !important",
              },
            }}
          />
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};
